"use client"

import { useAccount, useSimulateContract } from "wagmi"
import { parseEther } from "viem"
import { getChainId, getCollectibleCost } from "@/util/crypto"
import { erc721FactoryABI as factoryABI } from "@/util/typechain"
import { erc721ABI as abi } from "@/util/typechain"
import { zeroAddress } from "viem"
import { useGetMintTotalCost } from "@/hooks/collectibles/useGetMintFee"
import { CHAINS, CompletedCollectible } from "@/types/collectibles"
import { newCollectibleMetadata } from "@/lib/utils/collectibles"
import { Blog, Note } from "@types"

/**
 * Helper hook to get shared metadata for minting
 */
function useSharedMetadata({
  chain,
  collectibleCostWei,
}: {
  chain: CHAINS
  collectibleCostWei: bigint
}) {
  const chainId = getChainId(chain)
  const minterAddress = useAccount().address

  const { totalCost } = useGetMintTotalCost({
    collectibleCostWei,
    chain: chain,
  })

  if (!minterAddress)
    throw new Error("No minter address, but wallet should be connected!")

  return { chainId, minterAddress, totalCost }
}

/**
 * Given a completed collectible, creates a simulation
 * request. It can be passed to useMintCollectible
 * to actually mint the collectible.
 */
export function useCreateSimulateTxnExistingNFT({
  collectible,
}: {
  collectible: CompletedCollectible
}) {
  const { chainId, minterAddress, totalCost } = useSharedMetadata({
    chain: collectible.chain,
    collectibleCostWei: parseEther(collectible.costEth),
  })

  const referer = zeroAddress

  const simulateData = useSimulateContract({
    address: collectible.contractAddress as `0x${string}`,
    abi,
    chainId,
    functionName: "mintWithReferrer",
    args: [minterAddress || zeroAddress, referer],
    value: totalCost,
    query: {
      retry: 0,
    },
  })

  return simulateData
}

/**
 * Creates a simulation request for minting a new NFT.
 * It can be passed to useMintCollectible
 * to actually mint the collectible.
 */
export function useCreateSimulateTxnNewNFT({
  blog,
  post,
}: {
  blog: Blog
  post: Note
}) {
  const { contractMetadata, factoryAddress } = newCollectibleMetadata(
    blog,
    post
  )

  const collectibleCostWei = parseEther(
    getCollectibleCost(post, blog).toString()
  )

  const { chainId, minterAddress, totalCost } = useSharedMetadata({
    chain: contractMetadata.chain,
    collectibleCostWei,
  })

  const referer = zeroAddress
  const creatorReferrer = zeroAddress

  const mintData = {
    name_: contractMetadata.name,
    symbol_: contractMetadata.symbol,
    ownerAddr: contractMetadata.ownerAddress,
    minterAddr: minterAddress,
    creatorReferrerAddr: creatorReferrer,
    maxSupply: contractMetadata.supply,
    priceWei: contractMetadata.costWei,
  }

  console.log("Mint data", mintData, totalCost)

  const simulateData = useSimulateContract({
    address: factoryAddress as `0x${string}`,
    abi: factoryABI,
    chainId,
    functionName: "createAndMint",
    args: [mintData, referer, post.id, 0n, 0n],
    value: totalCost,
    query: {
      retry: 0,
    },
  })

  return simulateData
}
